import { Link, useNavigate } from 'react-router-dom';
import {
  CardWrapperWithHeader,
  getCurrencyFormat,
  LabelledItem,
} from '@rabbit/elements/shared-components';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { useContext, useEffect, useState } from 'react';
import { CaseflowContext } from '../../../context/CaseflowContext';
import Skeleton from 'react-loading-skeleton';
import { ConsumerHoldingSummaryShape } from '@rabbit/bizproc/client';
import { TenantLinkFieldName, FBD_Vendable_Private } from '@rabbit/data/types';
import { t } from 'i18next';
import { AppContext } from '@rabbit/app-context';
import { VehicleInfo } from '@rabbit/data/types';
import { Config_ClaimDetailViewType } from '@rabbit/config/enums';
import { getRetailerLabel } from '../../../utils/helpers';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ProductDetailsCardProps {}

export function getPurchaseLocationFromHolding(
  holdingData: ConsumerHoldingSummaryShape
): string {
  const locationName = holdingData?.full_purchase_location?.name;
  const otherLocation = holdingData?.purchase_location_other;

  if (!locationName) {
    return otherLocation || '-';
  }

  const town =
    holdingData.full_purchase_location?.addresses?.[0]?.town ||
    holdingData.full_purchase_location?.address?.town;

  return town ? `${locationName} - ${town}` : locationName;
}

export function ProductDetailsCard(props: ProductDetailsCardProps) {
  const navigate = useNavigate();
  const context = useContext(CaseflowContext);
  const tenantLink = t(TenantLinkFieldName);
  const { config } = useContext(AppContext);
  // TODO: full_purchase_location isn't available on holding proxy so holdingData has to be used for now. needs to be added to holdingProxy
  // you can get this for srvs with retailerAddresses on the warranties array, not sure about non-SRVs. Investigate - dc
  const { holdingData, holdingProxyData, caseFacts, operatingPersonaSingleKey } = context || {};

  const [retailerLabel, setRetailerLabel] = useState<string>('');

  // Fetch retailer label when consumer_holding_purchase_location is available
  useEffect(() => {
    const fetchRetailerLabel = async () => {
      if (!caseFacts?.consumer_holding_purchase_location) {
        setRetailerLabel('');
        return;
      }

      const label = await getRetailerLabel(
        caseFacts.consumer_holding_purchase_location
      );
      setRetailerLabel(label || '');
    };

    fetchRetailerLabel();
  }, [caseFacts?.consumer_holding_purchase_location]);

  if (!context)
    return (
      <CardWrapperWithHeader title={t('general.productDetails')}>
        <div className="font-nunito flex flex-col gap-3">
          <Skeleton count={2} containerClassName="max-w-[150px]" />
        </div>
      </CardWrapperWithHeader>
    );

  function getPurchaseLocation(): string {
    // Priority 1: Get from holdingData if available (most detailed)
    if (holdingData) {
      const locationFromHolding = getPurchaseLocationFromHolding(holdingData);
      if (locationFromHolding !== '-') {
        return locationFromHolding;
      }
    }

    // Priority 2: Get from fetched retailer label using consumer_holding_purchase_location
    if (retailerLabel) {
      return retailerLabel;
    }

    return '-';
  }

  function getProductName(): string {
    if (holdingData) {
      const brand = holdingData.brand ?? '';
      const title = holdingData.title ?? '';

      const shouldAppendBrand =
        config?.HOLDINGS?.PRODUCT?.APPEND_BRAND_TO_NAME === true;

      const nameToShow = shouldAppendBrand
        ? `${brand} ${title}`.trim()
        : (title || '').trim();

      return nameToShow || '-';
    }

    return caseFacts?.consumer_holding_name || '-';
  }

  const productName: string = getProductName();

  function getPurchasePrice(): string {
    // Compute paid price display and value (amount/currency)
    let display = '-';
    let paid: { amount: number; currency: string } | null = null;

    if (
      holdingData &&
      typeof holdingData.purchase_price !== 'string' &&
      holdingData.purchase_price?.amount
    ) {
      paid = {
        amount: Number(holdingData.purchase_price.amount),
        currency: String(holdingData.purchase_price.currency),
      };
      display = getCurrencyFormat(paid.amount, paid.currency);
    } else if (caseFacts?.consumer_holding_purchase_price) {
      if (typeof caseFacts.consumer_holding_purchase_price === 'string') {
        display = caseFacts.consumer_holding_purchase_price;
      } else if (
        typeof caseFacts.consumer_holding_purchase_price === 'object'
      ) {
        const priceObj = caseFacts.consumer_holding_purchase_price as any;
        if (priceObj.amount && priceObj.currency) {
          paid = {
            amount: Number(priceObj.amount),
            currency: String(priceObj.currency),
          };
          display = getCurrencyFormat(paid.amount, paid.currency);
        }
      }
    }

    // Optionally append RRP delta indicator when enabled and comparable
    if (
      config.HOLDINGS.REGISTRATION_DETAILS_VIEW.SHOW_RRP_DELTA &&
      paid?.amount &&
      vendableRrp?.amount &&
      paid.currency === vendableRrp.currency
    ) {
      if (paid.amount > vendableRrp.amount) {
        return `${display} (${t('RRP: ')}${getCurrencyFormat(vendableRrp.amount, vendableRrp.currency)})`;
      }
      if (paid.amount < vendableRrp.amount) {
        return `${display} (${t('RRP: ')}${getCurrencyFormat(vendableRrp.amount, vendableRrp.currency)})`;
      }
    }
    return display;
  }

  const [vendableRrp, setVendableRrp] = useState<{ amount: number; currency: string } | null>(
    null
  );
  useEffect(() => {
    let alive = true;
    async function loadRrp() {
      try {
        if (
          !config.HOLDINGS.REGISTRATION_DETAILS_VIEW.SHOW_RRP_DELTA ||
          !holdingProxyData?.vendable
        )
          return;
        const priv = await FBD_Vendable_Private.get(
          holdingProxyData.vendable as any
        );
        if (alive && (priv as any)?.rrp?.amount && (priv as any)?.rrp?.currency) {
          setVendableRrp({
            amount: Number((priv as any).rrp.amount),
            currency: String((priv as any).rrp.currency),
          });
        }
      } catch (e) {
        // ignore if not accessible
      }
    }
    loadRrp();
    return () => {
      alive = false;
    };
  }, [holdingProxyData?.vendable, config.HOLDINGS.REGISTRATION_DETAILS_VIEW.SHOW_RRP_DELTA]);

  function hasPurchasePrice(): boolean {
    return (
      (holdingData &&
        typeof holdingData.purchase_price !== 'string' &&
        !!holdingData.purchase_price?.amount) ||
      !!caseFacts?.consumer_holding_purchase_price
    );
  }

  function getSerialNumber(): string {
    const isSerialNumberVerified = holdingProxyData?.serialNumberVerified === true;
    let serialNumber = holdingProxyData?.serial;
    if (config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED) {
      serialNumber = isSerialNumberVerified ? serialNumber : serialNumber + ' (Unverified)';
    }
    return (
      serialNumber ||
      caseFacts?.consumer_holding_serial_number ||
      caseFacts?.serial_number ||
      '-'
    );
  }

  function hasSerialNumber(): boolean {
    return !!(
      holdingProxyData?.serial ||
      caseFacts?.serial_number ||
      caseFacts?.consumer_holding_serial_number
    );
  }

  const srvProductInfo =
    config.CLAIMS.CLAIM_DETAIL_VIEW.TYPE ===
      Config_ClaimDetailViewType.CAR_DETAILS && holdingData
      ? (holdingData?.srvInfo?.productInfo as VehicleInfo)
      : '';

  const onProductDetailsClick = () => {
    navigate(SAGE_ROUTE_NAME.REGISTRATIONS + '/' + holdingProxyData?.docid, {
      state: 'off',
    });
  };

  const isSRVTenant =
    tenantLink === 'NUCOVER' ||
    tenantLink === 'PINNACLEWARRANTIES' ||
    tenantLink === 'WARRANTYIRELAND';

  // Check if current persona can navigate to product details page
  const canNavigateToProductDetails =
    !config?.CLAIMS?.CLAIM_DETAIL_VIEW?.PRODUCT_DETAILS_SECTION ||
    config.CLAIMS.CLAIM_DETAIL_VIEW.PRODUCT_DETAILS_SECTION.length === 0 ||
    (operatingPersonaSingleKey &&
      config.CLAIMS.CLAIM_DETAIL_VIEW.PRODUCT_DETAILS_SECTION.includes(
        operatingPersonaSingleKey
      ));

  return (
    <CardWrapperWithHeader
      title={t('general.productDetails')}
      headerRight={
        isSRVTenant || !canNavigateToProductDetails ? null : (
          <ArrowTopRightOnSquareIcon
            className="text-primary-900 h-5 w-5 cursor-pointer"
            onClick={onProductDetailsClick}
          />
        )
      }
    >
      {!holdingData || !caseFacts ? (
        <div className="font-nunito flex flex-col gap-3">
          <Skeleton count={2} containerClassName="max-w-[150px]" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {srvProductInfo && (
            <LabelledItem
              label={t('general.registrationNo')}
              value={srvProductInfo.registrationNo}
            />
          )}
          <LabelledItem label={t('general.productName')} value={productName} />
          <LabelledItem
            label={t('general.purchaseLocation')}
            value={getPurchaseLocation()}
          />
          {hasSerialNumber() && (
            <LabelledItem
              label={t('general.productSerialNumber')}
              value={getSerialNumber()}
            />
          )}
          {hasPurchasePrice() && (
            <LabelledItem
              label={t('general.purchasePrice')}
              value={getPurchasePrice()}
            />
          )}
        </div>
      )}
    </CardWrapperWithHeader>
  );
}

export default ProductDetailsCard;
